import type { User } from "schemas/generated/user";

import { AGENT_ONBOARDING_JOIN_PATH } from "replo-utils/lib/agentApplicationPaths";
import {
  getDashboardLandingPath,
  getDashboardSurface,
} from "replo-utils/lib/dashboardRoutes";

import { getMarketingWorkflowLandingPath } from "./marketingWorkflow";

type LandingUser = Pick<
  User,
  | "defaultProjectId"
  | "defaultProjectHostingType"
  | "defaultProjectAgentEnabledAt"
>;

// user.get already resolved the last visited project (or its fallback); this picks its dashboard.
export function getProjectLandingPath(
  user: LandingUser | null | undefined,
): string | null {
  if (!user?.defaultProjectId) {
    return null;
  }
  return getDashboardLandingPath({
    projectId: user.defaultProjectId,
    surface: getDashboardSurface({
      hostingType: user.defaultProjectHostingType ?? null,
      agentEnabledAt: user.defaultProjectAgentEnabledAt ?? null,
    }),
  });
}

/** A teammate in an Agent project who hasn't said who they are yet, typically an invitee who just claimed their account. */
export function needsAgentJoinStep(
  user:
    | Pick<User, "firstName" | "lastName" | "defaultAgentProjectId">
    | null
    | undefined,
): boolean {
  return Boolean(
    user?.defaultAgentProjectId && !user.firstName && !user.lastName,
  );
}

/**
 * Where sign-in lands without an explicit `next`: the workflow a marketing link names, then the user's project.
 * Only a user with no project gets `projectlessPath`; "/" sends them to onboarding.
 * A new account that joined an Agent project without a name answers who they are first.
 */
export function getSignedInLandingPath({
  user,
  searchParams,
  projectlessPath,
  isNewAccount,
}: {
  user:
    | (LandingUser &
        Pick<User, "defaultAgentProjectId" | "firstName" | "lastName">)
    | null;
  searchParams: URLSearchParams;
  projectlessPath: string;
  isNewAccount: boolean;
}): { path: string; opensWorkflow: boolean } {
  if (isNewAccount && needsAgentJoinStep(user)) {
    return { path: AGENT_ONBOARDING_JOIN_PATH, opensWorkflow: false };
  }
  const workflowLandingPath = getMarketingWorkflowLandingPath({
    searchParams,
    agentProjectId: user?.defaultAgentProjectId,
  });
  if (workflowLandingPath) {
    return { path: workflowLandingPath, opensWorkflow: true };
  }
  return {
    path: getProjectLandingPath(user) ?? projectlessPath,
    opensWorkflow: false,
  };
}
